import React, { useState } from "react";
import { Download, FileText, Image as ImageIcon, Database } from "lucide-react";
import { dataClient as base44 } from "@/services/data";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function ExportMenu({ targetRef, nodes }) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    if (!targetRef?.current) return;
    setExporting(true);
    try {
      const { domToPng } = await import("modern-screenshot");
      const el = targetRef.current;

      // Let the offscreen export view finish laying out before capture.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      // Inline all <img> as data URLs via a backend function so cross-origin
      // photos (served from base44.app) render reliably in the capture.
      const imgs = Array.from(el.querySelectorAll("img"));
      const urls = imgs
        .map((i) => i.getAttribute("src"))
        .filter((u) => u && !u.startsWith("data:"));
      if (urls.length) {
        const res = await base44.functions.invoke("inlineImages", { urls });
        const map = res.data?.images || {};
        imgs.forEach((img) => {
          const src = img.getAttribute("src");
          if (src && map[src]) img.setAttribute("src", map[src]);
        });
      }

      // modern-screenshot renders via the browser's own engine (SVG foreignObject),
      // so modern CSS (rounded corners, alignment, fonts, color-mix) is pixel-accurate.
      const isDark = document.documentElement.getAttribute("data-theme") === "dark";
      const dataUrl = await domToPng(el, {
        scale: 2,
        backgroundColor: isDark ? "#0f172a" : "#ffffff",
      });

      if (format === "png") {
        const link = document.createElement("a");
        link.download = "selise-organogram.png";
        link.href = dataUrl;
        link.click();
      } else {
        const { jsPDF } = await import("jspdf");
        const img = new Image();
        img.src = dataUrl;
        await new Promise((res) => {
          img.onload = res;
          img.onerror = res;
        });
        const w = img.naturalWidth || el.scrollWidth * 2;
        const h = img.naturalHeight || el.scrollHeight * 2;
        const pdf = new jsPDF({
          orientation: w > h ? "landscape" : "portrait",
          unit: "px",
          format: [w, h],
        });
        pdf.addImage(dataUrl, "PNG", 0, 0, w, h);
        pdf.save("selise-organogram.pdf");
      }
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. Please try again.");
    }
    setExporting(false);
  };

  const handleExportAll = async () => {
    // TODO: the "Export all data" ZIP came from the base44 `exportAllData` backend
    // function, which has been removed. Re-wire this to the Blocks data gateway once
    // a backend exists. Until then, surface a clear notice instead of a failing call.
    console.warn('[data-stub] "exportAllData" is not wired to a backend yet.');
    alert("Full data export isn't available yet — it will be reconnected once the Blocks backend is set up.");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          title="Export"
          disabled={exporting}
          className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          {exporting ? (
            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport("pdf")}>
          <FileText className="w-4 h-4 mr-2" /> Export as PDF
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport("png")}>
          <ImageIcon className="w-4 h-4 mr-2" /> Export as Image (PNG)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportAll}>
          <Database className="w-4 h-4 mr-2" /> Export Data & Images (ZIP)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}